import { systemService } from '@/services/api';
import { useEffect, useState } from 'react';

const APP_VERSION = __APP_VERSION__;
const UNKNOWN_API_VERSION = 'unavailable';

let cachedApiVersion: string | null = null;
let pendingApiVersionRequest: Promise<string> | null = null;

const loadApiVersion = async (): Promise<string> => {
  if (cachedApiVersion) {
    return cachedApiVersion;
  }

  if (!pendingApiVersionRequest) {
    pendingApiVersionRequest = systemService
      .getVersion()
      .then((response) => {
        const resolvedVersion = response.apiVersion || response.version || UNKNOWN_API_VERSION;
        cachedApiVersion = resolvedVersion;
        return resolvedVersion;
      })
      .catch(() => UNKNOWN_API_VERSION)
      .finally(() => {
        pendingApiVersionRequest = null;
      });
  }

  return pendingApiVersionRequest;
};

/**
 * Returns build-time app version and API version fetched from the backend.
 */
export const useVersionInfo = () => {
  const [apiVersion, setApiVersion] = useState<string | null>(cachedApiVersion);
  const [isLoading, setIsLoading] = useState<boolean>(cachedApiVersion === null);

  useEffect(() => {
    let isMounted = true;

    if (cachedApiVersion) {
      setApiVersion(cachedApiVersion);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    loadApiVersion()
      .then((resolvedVersion) => {
        if (!isMounted) {
          return;
        }

        setApiVersion(resolvedVersion);
        setIsLoading(false);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setApiVersion(UNKNOWN_API_VERSION);
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    appVersion: APP_VERSION,
    apiVersion: apiVersion ?? UNKNOWN_API_VERSION,
    isLoading,
  };
};
